import type { CSSProperties, ReactNode } from 'react';

import { Show } from '@dvb/ui';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useThrottledCallback } from 'use-debounce';

import './index.css';

interface Props {
  btnClassName?: string;
  className?: string;
  isAbleToToggle?: boolean;
  maxLine?: number;
  text?: ReactNode | string;
  transitionMs?: number;
}

const LINE_CLAMP = String(9999);

export function DescriptionBlock({
  btnClassName = '',
  className = '',
  isAbleToToggle = true,
  maxLine = 4,
  text = '',
  transitionMs = 500,
}: Props) {
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [isEnableFeature, setIsEnableFeature] = useState(isAbleToToggle);
  const [isShowMore, setIsShowMore] = useState<boolean>();
  const [style, setStyle] = useState<CSSProperties>({});

  const handleMouseUp = useThrottledCallback(() => {
    if (isEnableFeature) {
      setIsShowMore((prev) => !prev);
    }
  }, transitionMs);

  useEffect(() => {
    const ref = contentRef.current;
    if (ref) {
      const isEnable = ref.scrollHeight > ref.clientHeight;
      setIsEnableFeature(isEnable);
      if (isEnable) {
        setStyle((prev) => ({
          maxHeight:
            Number(window.getComputedStyle(ref).lineHeight.replace('px', '')) *
            maxLine,
          ...prev,
        }));
      }
    }
  }, [maxLine]);

  useEffect(() => {
    const ref = contentRef.current;
    if (isShowMore == null || !ref) return;

    if (isShowMore === true) {
      setStyle((prev) => ({
        ...prev,
        maxHeight: ref.scrollHeight,
        WebkitLineClamp: LINE_CLAMP,
      }));
      return;
    }

    // Only change maxHeight first in order to have transition when hidden text
    setStyle((prev) => ({
      ...prev,
      maxHeight:
        Number(window.getComputedStyle(ref).lineHeight.replace('px', '')) *
        maxLine,
    }));
  }, [isShowMore, maxLine]);

  useEffect(() => {
    if (!isShowMore && style.WebkitLineClamp === LINE_CLAMP) {
      setTimeout(() => {
        setStyle((prev) => ({
          ...prev,
          WebkitLineClamp: String(maxLine),
        }));
      }, transitionMs);
    }
  }, [isShowMore, style, transitionMs, maxLine]);

  return (
    <div
      className={twMerge('', className)}
      onMouseUp={() => {
        handleMouseUp();
      }}
      role="button"
      tabIndex={-1}
    >
      <p
        className="overflow-hidden whitespace-pre-line break-words"
        ref={contentRef}
        style={{
          display: '-webkit-box',
          transition: `all ${transitionMs}ms`,
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: String(maxLine),
          ...style,
        }}
      >
        {text}
      </p>
      <Show when={isEnableFeature}>
        <div
          className={twMerge(
            'arrow-icon',
            btnClassName,
            isShowMore ? 'open' : ''
          )}
        >
          <span className="left-bar after:text-default-500" />
          <span className="right-bar after:text-default-500" />
        </div>
      </Show>
    </div>
  );
}
